import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { PlanGateBanner } from '../components/ui/plan-gate-banner';
import {
  Brain, Target, Zap, TrendingUp, ShieldCheck, Activity,
  BarChart3, PhoneCall, Mail, Loader2, Database,
  Lightbulb, Eye, MessageSquare, Award, Layers, GitBranch
} from 'lucide-react';
import api from '../lib/api';

const MODULE_COLORS = {
  sales: 'bg-blue-100 text-blue-700',
  service: 'bg-emerald-100 text-emerald-700',
  cadence: 'bg-violet-100 text-violet-700',
  accounts: 'bg-amber-100 text-amber-700',
};

const MEMORY_ICONS = {
  brain_decision: { icon: Zap, color: 'text-violet-600' },
  interaction: { icon: MessageSquare, color: 'text-sky-600' },
  strategy: { icon: Lightbulb, color: 'text-emerald-600' },
  observation: { icon: Eye, color: 'text-amber-600' },
};

function StatCard({ icon: Icon, label, value, sub, color = 'text-foreground' }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border border-border/60 bg-card hover:border-indigo-200 transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold tabular-nums leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground leading-tight mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ConfidenceBar({ high, medium, low, total }) {
  if (total === 0) return null;
  const hp = (high / total * 100);
  const mp = (medium / total * 100);
  return (
    <div className="flex h-2.5 rounded-full overflow-hidden bg-muted">
      <div className="bg-emerald-500 transition-all" style={{ width: `${hp}%` }} title={`High: ${high}`} />
      <div className="bg-amber-400 transition-all" style={{ width: `${mp}%` }} title={`Medium: ${medium}`} />
      <div className="bg-red-400 transition-all" style={{ width: `${100 - hp - mp}%` }} title={`Low: ${low}`} />
    </div>
  );
}

export default function BrainInsightsPage() {
  const [scorecard, setScorecard] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [memoryHealth, setMemoryHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planGate, setPlanGate] = useState(null); // Iter 59c — detect Growth plan gate

  const fetchAll = useCallback(async () => {
    try {
      const [sc, pt, st, dc, mh] = await Promise.all([
        api.get('/api/brain/insights/scorecard'),
        api.get('/api/brain/insights/patterns'),
        api.get('/api/brain/insights/strategy'),
        api.get('/api/brain/insights/decisions'),
        api.get('/api/brain/insights/memory-health'),
      ]);
      setScorecard(sc.data);
      setPatterns(pt.data);
      setStrategy(st.data);
      setDecisions(dc.data);
      setMemoryHealth(mh.data);
    } catch (e) {
      if (e?.response?.status === 403 && e.response.data?.detail?.required_plan) {
        setPlanGate(e.response.data.detail);
      }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Iter 59c — plan-gate check BEFORE loading skeleton (so Starter users see the upgrade card immediately after fetch)
  if (!loading && planGate) {
    return (
      <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="brain-insights-plan-gate">
        <PlanGateBanner
          gate={planGate}
          title="Brain Insights is a Growth feature"
          featureIcon={Brain}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="brain-insights-skeleton">
        <div className="h-7 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-2">
              <div className="h-4 w-20 bg-muted/60 rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
              <div className="h-3 w-24 bg-muted/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border p-6 space-y-3">
            <div className="h-5 w-40 bg-muted rounded animate-pulse" />
            <div className="h-48 w-full bg-muted/20 rounded animate-pulse" />
          </div>
          <div className="rounded-lg border p-6 space-y-3">
            <div className="h-5 w-36 bg-muted rounded animate-pulse" />
            <div className="h-48 w-full bg-muted/20 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const acc = scorecard?.prediction_accuracy || {};

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="brain-insights-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
          <Brain className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Brain Insights</h1>
          <p className="text-sm text-muted-foreground">Real intelligence metrics — what the Brain learned, what works, and where to improve</p>
        </div>
      </div>

      {/* Scorecard Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="brain-scorecard">
        <StatCard icon={Activity} label="Decisions Made" value={scorecard?.total_decisions || 0} sub={`${scorecard?.llm_calls || 0} LLM calls`} color="from-indigo-600 to-violet-600" />
        <StatCard icon={Target} label="Leads Analyzed" value={scorecard?.total_leads_analyzed || 0} sub={`$${((scorecard?.pipeline_value_influenced || 0) / 1000).toFixed(0)}K pipeline`} color="from-blue-600 to-sky-600" />
        <StatCard icon={Database} label="Memories" value={scorecard?.total_memories || 0} sub={`${scorecard?.total_recalls || 0} recalls`} color="from-violet-600 to-purple-600" />
        <StatCard icon={Zap} label="Auto Actions" value={scorecard?.total_autonomous_actions || 0} sub={`${scorecard?.total_alerts || 0} alerts`} color="from-amber-500 to-orange-500" />
        <StatCard
          icon={ShieldCheck}
          label="Prediction Accuracy"
          value={acc.accuracy_pct !== null ? `${acc.accuracy_pct}%` : '—'}
          sub={acc.sample_size > 0 ? `${acc.sample_size} outcomes tracked` : 'No outcomes yet'}
          color="from-emerald-600 to-teal-600"
        />
      </div>

      {/* Prediction Accuracy Detail (if data exists) */}
      {acc.sample_size > 0 && (
        <Card data-testid="prediction-accuracy-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Prediction Accuracy Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <p className="text-2xl font-bold text-emerald-700">{acc.avg_won_score}</p>
              <p className="text-xs text-muted-foreground">Avg score (Won leads)</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-2xl font-bold text-red-700">{acc.avg_lost_score}</p>
              <p className="text-xs text-muted-foreground">Avg score (Lost leads)</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-2xl font-bold text-blue-700">{acc.score_gap}</p>
              <p className="text-xs text-muted-foreground">Score gap (higher = better)</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-violet-50 border border-violet-200">
              <p className="text-2xl font-bold text-violet-700">{acc.won_count}/{acc.sample_size}</p>
              <p className="text-xs text-muted-foreground">Won / Total outcomes</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Decision Quality */}
        <Card data-testid="decision-quality-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-indigo-600" />
              Decision Quality by Module
              <Badge variant="secondary" className="text-[10px] ml-auto">{decisions?.total_decisions || 0} total</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {decisions?.by_module && Object.entries(decisions.by_module).map(([mod, data]) => (
              <div key={mod} className="space-y-1" data-testid={`module-${mod}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] capitalize ${MODULE_COLORS[mod] || 'bg-muted text-muted-foreground'}`}>{mod}</Badge>
                    <span className="text-xs text-muted-foreground">{data.count} decisions</span>
                  </div>
                  <span className="text-xs font-semibold">{Math.round(data.avg_confidence * 100)}% avg confidence</span>
                </div>
                <ConfidenceBar high={data.high_confidence} medium={data.medium_confidence} low={data.low_confidence} total={data.count} />
              </div>
            ))}
            {decisions?.outcome_tracking_pct > 0 && (
              <div className="pt-2 border-t">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Outcome tracking</span>
                  <span className="font-semibold">{decisions.outcome_tracking_pct}%</span>
                </div>
                <Progress value={decisions.outcome_tracking_pct} className="h-1.5 mt-1" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Memory Health */}
        <Card data-testid="memory-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Database className="h-4 w-4 text-violet-600" />
              Memory Health
              <Badge variant="secondary" className="text-[10px] ml-auto">{memoryHealth?.total_memories || 0} memories</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-violet-50">
                <p className="text-lg font-bold text-violet-700">{memoryHealth?.recall_rate || 0}%</p>
                <p className="text-[10px] text-muted-foreground">Recall Rate</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-sky-50">
                <p className="text-lg font-bold text-sky-700">{memoryHealth?.total_recalls || 0}</p>
                <p className="text-[10px] text-muted-foreground">Total Recalls</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-emerald-50">
                <p className="text-lg font-bold text-emerald-700">{memoryHealth?.coverage?.coverage_pct || 0}%</p>
                <p className="text-[10px] text-muted-foreground">Lead Coverage</p>
              </div>
            </div>

            {/* By type */}
            {memoryHealth?.by_type && Object.entries(memoryHealth.by_type).map(([type, data]) => {
              const conf = MEMORY_ICONS[type] || MEMORY_ICONS.observation;
              const Icon = conf.icon;
              return (
                <div key={type} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-3.5 w-3.5 ${conf.color}`} />
                    <span className="text-xs capitalize">{type.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span>{data.count} memories</span>
                    <span className="text-muted-foreground">{data.total_recalls} recalls</span>
                    {data.never_recalled > 0 && (
                      <Badge variant="outline" className="text-[9px] text-amber-600">{data.never_recalled} unused</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Pattern Discovery */}
        <Card data-testid="patterns-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              Pattern Discovery
              <Badge variant="secondary" className="text-[10px] ml-auto">{patterns?.total_leads || 0} leads analyzed</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {patterns?.patterns?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {patterns.patterns.map((p, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid={`pattern-${i}`}>
                    <Lightbulb className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-medium">{p.insight}</p>
                      <p className="text-[10px] text-muted-foreground">Confidence: {p.confidence}%</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">
                Patterns emerge when leads are marked Won or Lost. The Brain will detect what winning leads share.
              </p>
            )}

            {/* Win rate by source */}
            {patterns?.win_rate_by_source && Object.keys(patterns.win_rate_by_source).length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium mb-2">Win Rate by Source</p>
                <div className="space-y-1.5">
                  {Object.entries(patterns.win_rate_by_source)
                    .sort((a, b) => b[1].win_rate - a[1].win_rate)
                    .slice(0, 5)
                    .map(([src, data]) => (
                    <div key={src} className="flex items-center justify-between text-xs">
                      <span className="truncate">{src}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{data.total} leads</span>
                        <Badge variant={data.win_rate > 0 ? "default" : "secondary"} className="text-[10px]">
                          {data.win_rate}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Win rate by score bracket */}
            {patterns?.win_rate_by_score_bracket && (
              <div className="mt-3">
                <p className="text-xs font-medium mb-2">Win Rate by Brain Score</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {Object.entries(patterns.win_rate_by_score_bracket).map(([bracket, data]) => (
                    <div key={bracket} className={`text-center p-2 rounded-lg border ${
                      data.win_rate > 50 ? 'bg-emerald-50 border-emerald-200' :
                      data.win_rate > 0 ? 'bg-amber-50 border-amber-200' :
                      'bg-muted/30 border-border/50'
                    }`}>
                      <p className="text-sm font-bold">{data.win_rate}%</p>
                      <p className="text-[9px] text-muted-foreground">{bracket}</p>
                      <p className="text-[9px] text-muted-foreground">{data.total} leads</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Strategy Effectiveness */}
        <Card data-testid="strategy-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              Strategy Effectiveness
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Call outcomes */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <PhoneCall className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-medium">Voice Calls</span>
                <Badge variant="secondary" className="text-[10px] ml-auto">
                  {strategy?.total_calls || 0} total — {strategy?.call_success_rate || 0}% positive
                </Badge>
              </div>
              {strategy?.call_outcomes && Object.keys(strategy.call_outcomes).length > 0 ? (
                <div className="flex gap-1.5">
                  {Object.entries(strategy.call_outcomes).map(([outcome, data]) => (
                    <div key={outcome} className={`flex-1 text-center p-2 rounded-lg ${
                      outcome === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                      outcome === 'negative' ? 'bg-red-50 border border-red-200' :
                      'bg-muted/40 border border-border/50'
                    }`}>
                      <p className="text-sm font-bold">{data.count}</p>
                      <p className="text-[9px] text-muted-foreground capitalize">{outcome}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No call data yet</p>
              )}
            </div>

            {/* Cadence performance */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-xs font-medium">Cadences</span>
                <Badge variant="secondary" className="text-[10px] ml-auto">
                  {strategy?.cadences?.length || 0} active
                </Badge>
              </div>
              {strategy?.cadences?.length > 0 ? (
                <div className="space-y-1.5">
                  {strategy.cadences.slice(0, 4).map((cad, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[120px]">{cad.name || `Cadence ${i + 1}`}</span>
                        {cad.goal_boosted && <Badge className="bg-violet-100 text-violet-700 text-[9px]">Boosted</Badge>}
                      </div>
                      <div className="flex items-center gap-3 text-muted-foreground">
                        <span>{cad.steps} steps</span>
                        <span>{cad.emails_sent} sent</span>
                        <span>{cad.replies} replies</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No cadences yet</p>
              )}
            </div>

            {/* Top decision types */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-3.5 w-3.5 text-indigo-600" />
                <span className="text-xs font-medium">Top Brain Actions</span>
              </div>
              <div className="space-y-1">
                {strategy?.decision_types?.slice(0, 5).map((dt, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground truncate max-w-[180px]">{dt.action}</span>
                    <div className="flex items-center gap-2">
                      <span>{dt.count}x</span>
                      <Badge variant="outline" className="text-[9px]">{Math.round(dt.avg_confidence * 100)}%</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Decision Timeline */}
      {decisions?.timeline?.length > 0 && (
        <Card data-testid="decision-timeline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Layers className="h-4 w-4 text-indigo-600" />
              Decision Activity (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-20">
              {decisions.timeline.map((day, i) => {
                const maxDecisions = Math.max(...decisions.timeline.map(d => d.decisions));
                const height = Math.max((day.decisions / (maxDecisions || 1)) * 100, 8);
                const conf = day.avg_confidence;
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-t transition-all hover:opacity-80"
                    style={{
                      height: `${height}%`,
                      backgroundColor: conf >= 0.8 ? '#6366f1' : conf >= 0.5 ? '#f59e0b' : '#ef4444',
                    }}
                    title={`${day.date}: ${day.decisions} decisions (${Math.round(conf * 100)}% confidence)`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-muted-foreground">{decisions.timeline[0]?.date}</span>
              <div className="flex items-center gap-3 text-[9px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-indigo-500" />High conf</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" />Medium</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" />Low</span>
              </div>
              <span className="text-[9px] text-muted-foreground">{decisions.timeline[decisions.timeline.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
